import { Component, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';

import {
    PassBusinessResponse,
    PassBusinessGroupedResponse,
    PassBusinessCompanyView,
    PassBusinessItemView
} from 'src/app/models/pass-business.model';

import { SimplePaginationModel } from 'src/app/models/pagination.model';
import { PassBusinessService } from 'src/app/services/pass-business.service';
import { ResponseObject } from 'src/app/models/response-object.model';

@Component({
    selector: 'app-pass-business',
    templateUrl: './pass-business.component.html',
    styleUrls: ['./pass-business.component.scss']
})
export class PassBusinessComponent implements OnInit {
    allCompaniesPass: PassBusinessCompanyView[] = [];
    filteredCompaniesPass: PassBusinessCompanyView[] = [];
    companiesPass: PassBusinessCompanyView[] = [];

    pagination: SimplePaginationModel =
        new SimplePaginationModel(0, 6, 0, 0, 0);

    sizes = [6, 12, 36, 60];

    filterWord: string = '';
    timeout: any = null;

    modalCreate: boolean = false;
    edit: PassBusinessResponse = null;
    editCompanyName: string = '';

    gettingData: boolean = true;
    deleting: { [key: number]: boolean } = {};

    constructor(
        private toastService: ToastrService,
        private passBusinessService: PassBusinessService
    ) { }

    ngOnInit(): void {
        this.getPassBusiness();
    }

    getPassBusiness() {

        this.gettingData = true;

        this.passBusinessService.getVisiblePassBusiness().subscribe({
            next: (response: ResponseObject) => {

                const data = response.data as PassBusinessGroupedResponse[];

                this.allCompaniesPass = (data || []).map(group => ({
                    ...group,
                    expanded: false,
                    passes: (group.passes || []).map(pass => ({
                        ...pass,
                        visible: false,
                        deleting: false
                    }))
                }));

                this.applyFilters();
                this.gettingData = false;
            },
            error: (error: HttpErrorResponse) => {

                this.gettingData = false;
                this.allCompaniesPass = [];
                this.filteredCompaniesPass = [];
                this.companiesPass = [];

                this.toastService.error(
                    error.error?.message ||
                    'Error al obtener las contraseñas'
                );
            }
        });
    }

    filterCompany() {

        clearTimeout(this.timeout);

        this.timeout = setTimeout(() => {
            this.pagination.page = 0;
            this.applyFilters();
        }, 300);
    }

    applyFilters() {

        const value = (this.filterWord || '')
            .toLowerCase()
            .trim();

        this.filteredCompaniesPass = this.allCompaniesPass.filter(group => {

            const company = group.company;

            if (!company) {
                return false;
            }

            const name = (company.name || '').toLowerCase();
            const nit = (company.nit || '').toLowerCase();
            const code = (company.alphanumericId || '').toLowerCase();
            const id = String(company.id || '');

            return name.includes(value)
                || nit.includes(value)
                || code.includes(value)
                || id.includes(value);
        });

        this.getPaginationCompaniesPass();
    }

    getPaginationCompaniesPass() {

        const total = this.filteredCompaniesPass.length;
        const size = Number(this.pagination.size) || 6;

        const lastPage = Math.max(
            0,
            Math.ceil(total / size) - 1
        );

        if (this.pagination.page > lastPage) {
            this.pagination.page = lastPage;
        }

        if (this.pagination.page < 0) {
            this.pagination.page = 0;
        }

        const startIndex = this.pagination.page * size;
        const endIndex = startIndex + size;

        this.companiesPass = this.filteredCompaniesPass.slice(
            startIndex,
            endIndex
        );

        this.pagination.total = total;

        this.pagination.start = total > 0
            ? startIndex + 1
            : 0;

        this.pagination.end = Math.min(
            endIndex,
            total
        );
    }

    createPassBusiness() {

        this.edit = null;
        this.editCompanyName = '';
        this.modalCreate = true;
    }

    editPassBusiness(
        pass: PassBusinessResponse,
        company: PassBusinessCompanyView
    ) {
        this.edit = { ...pass };
        this.editCompanyName = company.company.name;
        this.modalCreate = true;
    }

    closeModal(changed: boolean) {

        this.modalCreate = false;
        this.edit = null;
        this.editCompanyName = '';

        if (changed) {
            this.getPassBusiness();
        }
    }

    toggleVisibility(pass: PassBusinessItemView) {
        pass.visible = !pass.visible;
    }

    toggleCompany(company: PassBusinessCompanyView) {
        company.expanded = !company.expanded;

        // Al cerrar, ocultar todas las contraseñas.
        if (!company.expanded) {
            company.passes.forEach((pass: PassBusinessItemView) => {
                pass.visible = false;
            });
        }
    }

    copy(copyText: string) {

        if (!copyText) {
            this.toastService.warning('No hay información para copiar');
            return;
        }

        const selBox = document.createElement('textarea');

        selBox.style.position = 'fixed';
        selBox.style.left = '0';
        selBox.style.top = '0';
        selBox.style.opacity = '0';

        selBox.value = copyText;

        document.body.appendChild(selBox);

        selBox.focus();
        selBox.select();

        try {
            const copied = document.execCommand('copy');

            if (copied) {
                this.toastService.success('Copiado al portapapeles');
            } else {
                this.toastService.error('No se pudo copiar');
            }
        } finally {
            document.body.removeChild(selBox);
        }
    }

    confirmDelete(pass: PassBusinessItemView) {
        pass.deleting = true;
    }

    cancelDelete(pass: PassBusinessItemView) {
        pass.deleting = false;
    }

    deletePassBusiness(pass: PassBusinessItemView) {

        if (this.deleting[pass.id]) {
            return;
        }

        this.deleting[pass.id] = true;

        this.passBusinessService.deletePassBusiness(pass.id).subscribe({
            next: () => {

                this.deleting[pass.id] = false;

                this.toastService.success(
                    'Contraseña eliminada correctamente'
                );

                this.getPassBusiness();
            },
            error: (error: HttpErrorResponse) => {

                this.deleting[pass.id] = false;

                this.toastService.error(
                    error.error?.message ||
                    'Error al eliminar la contraseña'
                );
            }
        });
    }

    getCompanyLogo(company: PassBusinessCompanyView): string {

        const logo = company.company?.logo;

        return logo
            ? 'https://s3.amazonaws.com/rrhh-images/company/logos/' + logo
            : 'assets/img/nophoto.svg';
    }

    logoError(event: Event) {

        const image = event.target as HTMLImageElement;
        image.onerror = null;
        image.src = 'assets/img/nophoto.svg';
    }

    trackByCompany(index: number, company: PassBusinessCompanyView) {
        return company.company?.id;
    }

    trackByPass(index: number, pass: PassBusinessResponse) {
        return pass.id;
    }
}